import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useDraggable } from 'react-use-draggable-scroll';
import { ButtonShape } from 'antd/lib/button/button';
import styled, { css } from 'styled-components';
import { ActiveTabParam, ApplicationUrlParam } from 'domains/catalog/Catalog.types';
import { theme, ThemeFontKeyType } from 'styles';
import { BlackButton, Box, GreyButton, WhiteButton } from 'UI';
import { OFFSET_PADDING, OFFSET_SUB_HEADER, useOffsetTop } from 'utils/hooks/useOffsetTop';
import { StickyBoxCustom } from '../StickyBox';

const halfCardCss = css`
  height: 46px;
  border-radius: 10px 10px 0 0;
  border: none;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  @media (max-width: 765px) {
    border-radius: 23px;
  }
`;

const GreyWhiteButton = styled.button<{ disabled?: boolean }>`
  height: 44px;
  border: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 0 20px 0 20px;
  font-weight: 600;
  font-size: 14px;
  background-color: ${({ disabled }) => (disabled ? theme.color.grey55 : theme.color.grey90)};
  color: ${({ disabled }) => (disabled ? theme.color.white : theme.color.brand_black)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  :hover {
    background-color: ${({ disabled }) => (disabled ? theme.color.grey55 : theme.color.white)};
  }
`;

const GreyWhiteButtonActive = styled.button`
  height: 44px;
  border: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 0 20px 0 20px;
  font-weight: 600;
  font-size: 14px;
  background-color: ${theme.color.white};
  color: ${theme.color.brand_black};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const GreyBlackButton = styled.button<{ disabled?: boolean }>`
  height: 60px;
  border: none;
  border-radius: 12px;
  padding: 0 32px 0 32px;
  font-weight: 600;
  font-size: 16px;
  background-color: ${({ disabled }) => (disabled ? theme.color.grey55 : theme.color.grey90)};
  color: ${({ disabled }) => (disabled ? theme.color.white : theme.color.brand_black)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  :hover {
    background-color: ${({ disabled }) => (disabled ? theme.color.grey55 : theme.color.grey75)};
  }

  :hover > div {
    transform: scale(1.05);
  }
`;

const GreyBlackButtonActive = styled.button`
  height: 60px;
  border: none;
  border-radius: 12px;
  padding: 0 32px 0 32px;
  font-weight: 600;
  font-size: 16px;
  background-color: ${theme.color.brand_black};
  color: ${theme.color.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const BlackHalfContainer = styled.div`
  padding-top: 8px;
  background-color: ${({ theme: t }) => t.color.white};

  @media (max-width: 765px) {
    background-color: ${({ theme: t }) => t.color.brand_black};
    padding: 8px 0;
  }
`;

const TAB_SCROLLBAR_HEIGHT = 6;
const TAB_SCROLLBAR_GAP = 6;
const TAB_SCROLLBAR_MIN_THUMB_PERCENT = 8;

export const getTabScrollbarThumbMetrics = (scrollLeft: number, scrollWidth: number, clientWidth: number) => {
  const maxScroll = scrollWidth - clientWidth;
  const thumbWidthPercent = Math.max((clientWidth / scrollWidth) * 100, TAB_SCROLLBAR_MIN_THUMB_PERCENT);
  const thumbLeftPercent = maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - thumbWidthPercent) : 0;

  return { thumbWidthPercent, thumbLeftPercent };
};

const DragScrollContainer = styled.div<{ gap: number; scrollable?: boolean }>`
  display: flex;
  gap: ${({ gap }) => gap}px;
  overflow-x: ${({ scrollable }) => (scrollable ? 'auto' : 'hidden')};
  overflow-y: hidden;
  flex-wrap: ${({ scrollable }) => (scrollable ? 'nowrap' : 'wrap')};

  ${({ scrollable }) =>
    scrollable &&
    css`
      width: 100%;
      min-width: 0;
      scrollbar-width: none;

      > div {
        flex-shrink: 0;
      }

      &::-webkit-scrollbar {
        display: none;
      }
    `}
`;

const TabScrollbarTrack = styled.div`
  position: relative;
  height: ${TAB_SCROLLBAR_HEIGHT}px;
  margin-top: ${TAB_SCROLLBAR_GAP}px;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.color.grey25};
  cursor: pointer;
`;

const TabScrollbarThumb = styled.div<{ $widthPercent: number; $leftPercent: number; $isDragging: boolean }>`
  position: absolute;
  top: 0;
  left: ${({ $leftPercent }) => $leftPercent}%;
  height: 100%;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.color.grey55};
  width: ${({ $widthPercent }) => $widthPercent}%;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
`;

const BlackHalfCardButton = styled.button<{ disabled?: boolean }>`
  ${halfCardCss};
  background-color: ${({ theme: t, disabled }) => (disabled ? t.color.grey80 : t.color.brand_black)};
  color: ${({ theme: t, disabled }) => (disabled ? t.color.grey55 : t.color.white)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  :hover {
    background-color: ${({ theme: t, disabled }) => (disabled ? t.color.grey80 : t.color.grey10)};
  }

  @media (max-width: 765px) {
    background-color: ${({ theme: t, disabled }) => (disabled ? t.color.grey80 : t.color.grey35)};

    :hover {
      background-color: ${({ theme: t, disabled }) => (disabled ? t.color.grey80 : t.color.grey55)};
    }
  }
`;

const WhiteHalfCardButton = styled.button`
  ${halfCardCss};
  background-color: ${({ theme: t }) => t.color.white};
  color: ${({ theme: t }) => t.color.brand_black};
  border: 1.5px solid ${({ theme: t }) => t.color.grey75};
  cursor: pointer;

  :hover {
    border-color: ${({ theme: t }) => t.color.grey45};
    background-color: ${({ theme: t }) => t.color.grey95};
  }
`;

const DisplayCard = styled.div`
  ${halfCardCss};
  background-color: ${({ theme: t }) => t.color.white};
  color: ${({ theme: t }) => t.color.brand_black};
  border: 1.5px solid ${({ theme: t }) => t.color.grey75};
  cursor: default;
  user-select: none;
  min-width: 0;
  flex-shrink: 1;
  justify-content: flex-start;
  overflow: hidden;

  @media (max-width: 765px) {
    border: none;
  }
`;

const RedHalfCardButton = styled.button<{ staticWithColor?: string }>`
  ${halfCardCss};
  background-color: ${({ staticWithColor }) => staticWithColor};
  border-color: ${({ staticWithColor }) => staticWithColor};
  color: ${({ theme: styledTheme }) => styledTheme.color.white};
`;

const BlackHalfCardButtonActive = styled.button`
  ${halfCardCss};
  background-color: ${({ theme: styledTheme }) => styledTheme.color.white};
  color: ${({ theme: styledTheme }) => styledTheme.color.brand_black};
  border: 1.5px solid ${({ theme: styledTheme }) => styledTheme.color.grey75};
  cursor: pointer;

  /* Override child text/icon colors — labels use lead_text_white (white) by default */
  span {
    color: ${({ theme: styledTheme }) => styledTheme.color.brand_black};
  }
  .icon g,
  .icon use {
    fill: ${({ theme: styledTheme }) => styledTheme.color.brand_black};
  }

  @media (max-width: 765px) {
    border: none;
  }
`;

const BlackPillContainer = styled.div`
  width: 100%;
  min-width: 0;
  padding: 4px 0;
  background-color: ${({ theme: t }) => t.color.brand_black};

  ${BlackHalfCardButton} {
    height: 32px;
    padding: 0 14px;
    border-radius: 23px;
    background-color: ${({ theme: t }) => t.color.grey35};

    :hover {
      background-color: ${({ theme: t }) => t.color.grey55};
    }
  }

  ${BlackHalfCardButtonActive} {
    height: 32px;
    padding: 0 14px;
    border-radius: 23px;
    border: none;
  }

  ${RedHalfCardButton} {
    height: 32px;
    padding: 0 14px;
    border-radius: 23px;
  }

  ${WhiteHalfCardButton} {
    height: 32px;
    padding: 0 14px;
    border-radius: 23px;
  }
`;

export interface LinkTabItem {
  key: number;
  link: string;
  /** Optional Route path pattern; defaults to `link`. Use when nav URL must stay clean but Route needs path params. */
  routePath?: string;
  uparam?: URLSearchParams;
  additionalAction?: () => void;
  label: ReactNode;
  labelActive?: ReactNode;
  component?: ReactNode;
  isDisabled?: boolean;
  staticWithColor?: string;
  allowClickOnStatic?: boolean;
  dataCy?: string;
  light?: boolean;
  isDisplay?: boolean;
}

export type LinkTabsVariant = 'greyOutlined' | 'white' | 'blackHalf' | 'blackPill' | 'greyWhite' | 'greyBlack';

export type LinkTabsSection = 'full' | 'tabBar' | 'routes';

export interface LinkTabProps {
  sticky?: boolean;
  items?: LinkTabItem[];
  variant?: LinkTabsVariant;
  variantActive?: 'black' | 'greyOutlined';
  shape?: ButtonShape;
  textType?: ThemeFontKeyType;
  textTypeActive?: ThemeFontKeyType;
  urlParam?: ApplicationUrlParam;
  absolute?: boolean;
  gap?: number;
  scrollable?: boolean;
  section?: LinkTabsSection;
}

export const LinkTabs = ({
  sticky,
  items,
  urlParam = ActiveTabParam,
  shape = 'round',
  variant = 'greyOutlined',
  absolute,
  gap,
  scrollable,
  section = 'full',
}: LinkTabProps) => {
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);
  const stickyOffset = useOffsetTop('RENAULT_CATALOG') + OFFSET_SUB_HEADER;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isDraggingThumb, setIsDraggingThumb] = useState(false);
  const [scrollMetrics, setScrollMetrics] = useState({
    scrollLeft: 0,
    scrollWidth: 1,
    clientWidth: 1,
  });
  const thumbDragStartXRef = useRef(0);
  const thumbDragStartScrollLeftRef = useRef(0);
  const enableDragScroll = Boolean(scrollable) && section !== 'routes';
  const { events } = useDraggable(scrollContainerRef as React.MutableRefObject<HTMLElement>, {
    isMounted: enableDragScroll,
  });

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !scrollable) {
      setHasOverflow(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setHasOverflow(scrollWidth > clientWidth + 1);
    setScrollMetrics({ scrollLeft, scrollWidth, clientWidth });
  }, [scrollable]);

  useEffect(() => {
    if (!scrollable) {
      return undefined;
    }

    const runUpdate = () => {
      requestAnimationFrame(updateScrollState);
    };

    runUpdate();
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(runUpdate);
    resizeObserver.observe(container);
    window.addEventListener('resize', runUpdate);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', runUpdate);
    };
  }, [scrollable, items, updateScrollState]);

  const isActive = (link: string, uparam: URLSearchParams | undefined): boolean => {
    if (!uparam) {
      return window.location.pathname.includes(getUrl(link));
    }
    return urlParams.get(urlParam) === uparam.get(urlParam);
  };

  const getUrl = (itemLink: string) => (absolute ? '' : url) + itemLink;

  const handleClickAction = ({
    uparam,
    additionalAction,
    link,
  }: {
    uparam?: URLSearchParams;
    additionalAction?: () => void;
    link: string;
  }) => {
    const params = uparam ? `?${uparam}` : '';
    additionalAction?.();
    history.push(`${getUrl(link)}${params}`);
  };

  const renderActiveButton = ({ uparam, link, additionalAction, label, dataCy, light }: LinkTabItem, key: number) => {
    function handleClick() {
      handleClickAction({ uparam, additionalAction, link });
    }

    const activeButtonProps = { onClick: handleClick, 'data-cy': dataCy };

    switch (variant) {
      case 'blackHalf':
      case 'blackPill':
        if (light) {
          return (
            // NOSONAR - Tab clicks require runtime navigation callback with optional additionalAction.
            <WhiteHalfCardButton key={key} {...activeButtonProps}>
              {label}
            </WhiteHalfCardButton>
          );
        }
        return (
          <BlackHalfCardButtonActive key={key} {...activeButtonProps}>
            {label}
          </BlackHalfCardButtonActive>
        );
      case 'greyWhite':
        return (
          <GreyWhiteButtonActive key={key} {...activeButtonProps}>
            {label}
          </GreyWhiteButtonActive>
        );
      case 'greyBlack':
        return (
          <GreyBlackButtonActive key={key} {...activeButtonProps}>
            {label}
          </GreyBlackButtonActive>
        );
      default: {
        return (
          <BlackButton key={key} size={'large'} shape={shape} {...activeButtonProps}>
            {label}
          </BlackButton>
        );
      }
    }
  };

  const renderStaticButton = (
    { label, dataCy, link, uparam, additionalAction, allowClickOnStatic }: LinkTabItem,
    key: number,
    staticWithColor?: string,
  ) => {
    const canHandleClick = Boolean(allowClickOnStatic && link);

    function handleStaticClick() {
      if (!link) {
        return;
      }
      handleClickAction({ uparam, additionalAction, link });
    }

    return (
      <RedHalfCardButton
        key={key}
        data-cy={dataCy}
        staticWithColor={staticWithColor}
        onClick={canHandleClick ? handleStaticClick : undefined}
        style={canHandleClick ? { cursor: 'pointer' } : undefined}
      >
        {label}
      </RedHalfCardButton>
    );
  };

  const renderNonActiveButton = (
    { uparam, link, additionalAction, label, isDisabled = false, dataCy, light }: LinkTabItem,
    key: number,
  ) => {
    function handleClick() {
      handleClickAction({ uparam, additionalAction, link });
    }

    const buttonProps = { onClick: handleClick, 'data-cy': dataCy };

    switch (variant) {
      case 'white':
        return (
          <WhiteButton key={key} size={'large'} shape={shape} disabled={isDisabled} {...buttonProps}>
            {label}
          </WhiteButton>
        );
      case 'blackHalf':
      case 'blackPill':
        if (light) {
          return (
            <WhiteHalfCardButton key={key} {...buttonProps}>
              {label}
            </WhiteHalfCardButton>
          );
        }
        return (
          <BlackHalfCardButton key={key} disabled={isDisabled} {...buttonProps}>
            {label}
          </BlackHalfCardButton>
        );
      case 'greyWhite':
        return (
          <GreyWhiteButton key={key} disabled={isDisabled} {...buttonProps}>
            {label}
          </GreyWhiteButton>
        );
      case 'greyBlack':
        return (
          <GreyBlackButton key={key} disabled={isDisabled} {...buttonProps}>
            <div>{label}</div>
          </GreyBlackButton>
        );
      default:
        return (
          <GreyButton key={key} size={'large'} shape={shape} disabled={isDisabled} {...buttonProps}>
            {label}
          </GreyButton>
        );
    }
  };

  const renderScrollTrack = () => {
    if (!scrollable || !hasOverflow) {
      return null;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollMetrics;
    const { thumbWidthPercent, thumbLeftPercent } = getTabScrollbarThumbMetrics(scrollLeft, scrollWidth, clientWidth);

    const scrollToTrackPosition = (clientX: number) => {
      const container = scrollContainerRef.current;
      const track = scrollbarTrackRef.current;
      if (!container || !track) {
        return;
      }

      const currentMaxScroll = container.scrollWidth - container.clientWidth;
      if (currentMaxScroll <= 0) {
        return;
      }

      const trackRect = track.getBoundingClientRect();
      const thumbWidthPx = (thumbWidthPercent / 100) * trackRect.width;
      const maxThumbTravelPx = Math.max(trackRect.width - thumbWidthPx, 0);
      if (maxThumbTravelPx <= 0) {
        container.scrollLeft = 0;
        return;
      }

      const clickX = clientX - trackRect.left;
      const targetThumbLeftPx = Math.min(Math.max(clickX - thumbWidthPx / 2, 0), maxThumbTravelPx);
      const targetScrollLeft = (targetThumbLeftPx / maxThumbTravelPx) * currentMaxScroll;
      container.scrollLeft = targetScrollLeft;
    };

    const handleTrackMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      scrollToTrackPosition(event.clientX);
    };

    const handleThumbMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDraggingThumb(true);
      thumbDragStartXRef.current = event.clientX;
      thumbDragStartScrollLeftRef.current = scrollContainerRef.current?.scrollLeft ?? 0;
    };

    return (
      <TabScrollbarTrack ref={scrollbarTrackRef} data-cy={'link-tabs-scrollbar'} onMouseDown={handleTrackMouseDown}>
        <TabScrollbarThumb
          $widthPercent={thumbWidthPercent}
          $leftPercent={thumbLeftPercent}
          $isDragging={isDraggingThumb}
          onMouseDown={handleThumbMouseDown}
        />
      </TabScrollbarTrack>
    );
  };

  useEffect(() => {
    if (!isDraggingThumb) {
      return undefined;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const container = scrollContainerRef.current;
      const track = scrollbarTrackRef.current;
      if (!container || !track) {
        return;
      }

      const maxScroll = container.scrollWidth - container.clientWidth;
      if (maxScroll <= 0) {
        return;
      }

      const trackRect = track.getBoundingClientRect();
      const thumbWidthPx = (container.clientWidth / container.scrollWidth) * trackRect.width;
      const maxThumbTravelPx = Math.max(trackRect.width - thumbWidthPx, 0);
      if (maxThumbTravelPx <= 0) {
        return;
      }

      const deltaX = event.clientX - thumbDragStartXRef.current;
      const scrollDelta = (deltaX / maxThumbTravelPx) * maxScroll;
      container.scrollLeft = thumbDragStartScrollLeftRef.current + scrollDelta;
    };

    const handleMouseUp = () => {
      setIsDraggingThumb(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingThumb]);

  const renderTabs = () => {
    return (
      <DragScrollContainer
        ref={scrollContainerRef}
        {...events}
        gap={gap ?? 16}
        scrollable={scrollable}
        data-cy={scrollable ? 'link-tabs-scroll' : undefined}
        onScroll={scrollable ? updateScrollState : undefined}
      >
        {items
          ?.filter((it) => it.label)
          .map((item) => {
            if (item.isDisplay) {
              const displayActive = isActive(item.link, item.uparam);
              if (displayActive) {
                return (
                  <div key={item.key}>
                    <DisplayCard data-cy={item.dataCy}>{item.labelActive ?? item.label}</DisplayCard>
                  </div>
                );
              }
              return <div key={item.key}>{renderNonActiveButton(item, item.key)}</div>;
            }
            if (item.staticWithColor) {
              return <div key={item.key}>{renderStaticButton(item, item.key, item.staticWithColor)}</div>;
            }
            return (
              item &&
              (isActive(item.link, item.uparam) ? (
                <div key={item.key}>{renderActiveButton(item, item.key)}</div>
              ) : (
                <div key={item.key}>{renderNonActiveButton(item, item.key)}</div>
              ))
            );
          })}
      </DragScrollContainer>
    );
  };

  const renderTabsContainer = () => {
    switch (variant) {
      case 'blackPill':
        return (
          <BlackPillContainer>
            {renderTabs()}
            {renderScrollTrack()}
          </BlackPillContainer>
        );
      case 'blackHalf':
        return <BlackHalfContainer>{renderTabs()}</BlackHalfContainer>;
      case 'greyWhite':
        return <Box background={theme.color.grey90}>{renderTabs()}</Box>;
      case 'greyBlack':
        return <div style={{ padding: 8, borderRadius: 12, backgroundColor: theme.color.grey90 }}>{renderTabs()}</div>;
      default:
        return <Box background={theme.color.white}>{renderTabs()}</Box>;
    }
  };

  const renderRoutes = () => (
    <Switch>
      {items?.map((item) =>
        item ? (
          <Route key={`tab-${item.key}`} path={(absolute ? '' : path) + (item.routePath ?? item.link)}>
            {item.component || null}
          </Route>
        ) : null,
      )}
    </Switch>
  );

  const renderTabBar = () =>
    sticky ? (
      <StickyBoxCustom offsetTop={stickyOffset} zIndex={799}>
        <Box height={OFFSET_PADDING} background={theme.color.white} />
        {renderTabsContainer()}
      </StickyBoxCustom>
    ) : (
      renderTabsContainer()
    );

  if (section === 'tabBar') {
    return renderTabBar();
  }

  if (section === 'routes') {
    return renderRoutes();
  }

  return (
    <>
      {renderTabBar()}
      {renderRoutes()}
    </>
  );
};
